<!-- ─── Loading State ─────────────────────────────────────────────────────── -->
<div *ngIf="isLoading" class="rdv-loading">
  <mat-spinner diameter="48"></mat-spinner>
  <p>Loading details…</p>
</div>

<!-- ─── Error State ───────────────────────────────────────────────────────── -->
<div *ngIf="!isLoading && errorMessage" class="rdv-error">
  <mat-icon>error_outline</mat-icon>
  <p>{{ errorMessage }}</p>
  <button mat-stroked-button (click)="close()"><mat-icon>arrow_back</mat-icon> Back</button>
</div>

<!-- ─── Main Content ──────────────────────────────────────────────────────── -->
<ng-container *ngIf="!isLoading && !errorMessage && detailView">

  <!-- ── Hero Banner ───────────────────────────────────────────────────────── -->
  <div class="rdv-hero">
    <div class="rdv-hero-left">
      <button mat-icon-button class="rdv-back-btn" (click)="close()" matTooltip="Back to list">
        <mat-icon>arrow_back</mat-icon>
      </button>
      <div class="rdv-hero-text">
        <div class="rdv-hero-eyebrow">Enquiry No.</div>
        <!--
          Tooltip on heading enables reading the full ID even when it is clipped.
          Pattern: every element with text-overflow: ellipsis must carry [matTooltip].
        -->
        <h2 class="rdv-hero-enq"
          [matTooltip]="detailView.requisition.enquiryId || detailView.requisition.docNo"
          [matTooltipShowDelay]="600">
          {{ detailView.requisition.enquiryId || detailView.requisition.docNo || 'Requisition' }}
        </h2>
        <!--
          badge icon  → customer identity (distinct from trainer's school icon)
          domain icon → organisation type (distinct from the person icon)
        -->
        <div class="rdv-hero-customer" *ngIf="detailView.requisition.customerName">
          <mat-icon class="rdv-hero-customer-icon">badge</mat-icon>
          <span [matTooltip]="detailView.requisition.customerName" [matTooltipShowDelay]="600">
            {{ detailView.requisition.customerName }}
          </span>
          <ng-container *ngIf="detailView.requisition.customerType">
            <span class="rdv-hero-customer-sep">·</span>
            <mat-icon class="rdv-hero-customer-type-icon">domain</mat-icon>
            <span class="rdv-hero-customer-type"
              [matTooltip]="detailView.requisition.customerType" [matTooltipShowDelay]="600">
              {{ detailView.requisition.customerType }}
            </span>
          </ng-container>
        </div>
      </div>
    </div>
    <div class="rdv-hero-right">
      <span *ngIf="detailView.requisition.status"
        class="rdv-status-badge rdv-status-badge--{{ detailView.requisition.status | lowercase }}"
        [matTooltip]="detailView.requisition.status" [matTooltipShowDelay]="600">
        <span class="rdv-status-dot"></span>{{ detailView.requisition.status }}
      </span>
      <button mat-stroked-button class="rdv-forward-btn" (click)="openForwardPopup()" matTooltip="Forward requisition">
        <mat-icon>open_in_new</mat-icon> Forward
      </button>
    </div>
  </div>

  <!-- ── Metadata strip ────────────────────────────────────────────────────── -->
  <!--
    Each meta item has a distinct contextual icon:
      rate_review  → authorship / "created by"
      event        → calendar date
      receipt_long → document reference number
      group        → participant headcount
      layers       → stacked resource configurations
  -->
  <div class="rdv-meta-strip">
    <span class="rdv-meta-item">
      <mat-icon class="rdv-meta-icon">rate_review</mat-icon>
      <span>Created by <strong>{{ detailView.requisition.createdByName || '—' }}</strong></span>
    </span>
    <span class="rdv-meta-sep">·</span>
    <span class="rdv-meta-item">
      <mat-icon class="rdv-meta-icon">event</mat-icon>
      <span>{{ detailView.requisition.createdOn || '—' }}</span>
    </span>
    <ng-container *ngIf="detailView.requisition.docNo">
      <span class="rdv-meta-sep">·</span>
      <span class="rdv-meta-item">
        <mat-icon class="rdv-meta-icon">receipt_long</mat-icon>
        <span class="rdv-meta-docno">{{ detailView.requisition.docNo }}</span>
      </span>
    </ng-container>
    <ng-container *ngIf="detailView.class && detailView.class.participantCount != null">
      <span class="rdv-meta-sep">·</span>
      <span class="rdv-meta-item">
        <mat-icon class="rdv-meta-icon">group</mat-icon>
        <span>{{ detailView.class.participantCount }} Participant{{ detailView.class.participantCount !== 1 ? 's' : '' }}</span>
      </span>
    </ng-container>
    <span class="rdv-meta-sep">·</span>
    <span class="rdv-meta-item">
      <mat-icon class="rdv-meta-icon">layers</mat-icon>
      <span>{{ detailView.templates.length }} Resource Config{{ detailView.templates.length !== 1 ? 's' : '' }}</span>
    </span>
  </div>

  <!-- ── Notes: Special Requirements + Final Comments side-by-side ─────────── -->
  <!--
    When both notes are present they share a row, each with an individual scrollbar
    if its content exceeds max-height.  When only one is present it spans full width.
  -->
  <div class="rdv-notes-row"
    *ngIf="detailView.requisition.specialRequirements || detailView.requisition.finalComments">
    <div class="rdv-note" *ngIf="detailView.requisition.specialRequirements">
      <span class="rdv-note-label">Special Requirements</span>
      <p class="rdv-note-text">{{ detailView.requisition.specialRequirements }}</p>
    </div>
    <div class="rdv-note rdv-note--info" *ngIf="detailView.requisition.finalComments">
      <span class="rdv-note-label">Final Comments</span>
      <p class="rdv-note-text">{{ detailView.requisition.finalComments }}</p>
    </div>
  </div>

  <!-- ── Sections ─────────────────────────────────────────────────────────── -->
  <div class="rdv-content">

    <!-- ── Class / Batch ──────────────────────────────────────────────────── -->
    <section class="rdv-section">
      <div class="rdv-section-header">
        <span class="rdv-section-title">Class / Batch</span>
        <!--
          Border colour = confirmation state (confirm=green, pending=amber, cancelled=red).
          Dot colour    = schedule stage   (upcoming=blue,   ongoing=green, completed=grey).
        -->
        <span *ngIf="classStatus"
          class="rdv-class-badge rdv-class-badge--{{ classStageKey }} rdv-class-badge--confirm-{{ classConfirmKey }}">
          <span class="rdv-class-dot"></span>{{ classStatus }}
        </span>
      </div>
      <div class="rdv-section-rule"></div>

      <ng-container *ngIf="detailView.class; else noClass">
        <!-- Card border gives the class body visual weight so it doesn't float. -->
        <div class="rdv-class-card">
          <div class="rdv-section-body">
            <!--
              rdv-class-summary row:
                rdv-batch-name       → batch title (already has tooltip)
                rdv-class-trainer    → school icon (distinct from customer badge icon)
                rdv-class-participants → group icon
            -->
            <div class="rdv-class-summary">
              <span class="rdv-batch-name" *ngIf="detailView.class.batchTitle"
                [matTooltip]="detailView.class.batchTitle" [matTooltipShowDelay]="400">
                {{ detailView.class.batchTitle }}
              </span>
              <span class="rdv-class-trainer"
                *ngIf="detailView.class.trainers?.length"
                [matTooltip]="formatTrainers(detailView.class.trainers)" [matTooltipShowDelay]="400">
                <!-- school icon → trainer/educator (distinct from customer's badge icon) -->
                <mat-icon class="rdv-inline-icon">school</mat-icon>
                {{ formatTrainers(detailView.class.trainers) }}
              </span>
              <span class="rdv-class-participants"
                *ngIf="detailView.class.participantCount != null">
                <!-- group icon → headcount (distinct from trainer school icon) -->
                <mat-icon class="rdv-inline-icon rdv-icon--participants">group</mat-icon>
                {{ detailView.class.participantCount }} Participant{{ detailView.class.participantCount !== 1 ? 's' : '' }}
              </span>
            </div>

            <!-- Sessions -->
            <div class="rdv-sessions" *ngIf="detailView.class.dateRanges?.length">
              <mat-icon class="rdv-inline-icon rdv-sessions-icon">calendar_today</mat-icon>
              <span class="rdv-kv-label">Sessions</span>
              <div class="rdv-session-list">
                <span class="rdv-session-pill" *ngFor="let dr of detailView.class.dateRanges">
                  <ng-container *ngIf="dr.title">{{ dr.title }}&ensp;</ng-container>
                  {{ dr.startDate }}<ng-container *ngIf="dr.startDate !== dr.endDate"> – {{ dr.endDate }}</ng-container>
                  <ng-container *ngIf="dr.startTime">
                    &ensp;{{ dr.startTime }}<ng-container *ngIf="dr.endTime"> – {{ dr.endTime }}</ng-container>
                  </ng-container>
                </span>
              </div>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-template #noClass>
        <div class="rdv-empty-state">No class created yet. Forward this requisition to create a batch.</div>
      </ng-template>
    </section>

    <!-- ── Resource Configurations ────────────────────────────────────────── -->
    <section class="rdv-section">
      <div class="rdv-section-header">
        <span class="rdv-section-title">Resource Configurations</span>
        <span class="rdv-section-count" *ngIf="detailView.templates.length">({{ detailView.templates.length }})</span>
      </div>
      <div class="rdv-section-rule"></div>

      <ng-container *ngIf="hasTemplates; else noConfigs">
        <div class="rdv-rg-list">
          <div class="rdv-rg-block" *ngFor="let group of resourceGroups">

            <!-- Two-column: Template | Policy -->
            <div class="rdv-rg-columns">

              <!-- ── Template column ────────────────────────────────────── -->
              <div class="rdv-rg-col">
                <div class="rdv-rg-col-heading">
                  <span class="rdv-rg-type-tag">{{ group.typeLabel }}</span>Template
                </div>
                <div class="rdv-rg-col-rule"></div>

                <!-- Template name with UA status pill inline -->
                <div class="rdv-rg-name-row">
                  <span class="rdv-rg-name" [matTooltip]="group.template.name" [matTooltipShowDelay]="400">
                    {{ group.template.name }}
                  </span>
                  <span *ngIf="isUaTemplate(group.template) && group.template.status"
                    class="rdv-ua-status rdv-ua-status--{{ group.template.status | lowercase }}"
                    [matTooltip]="group.template.status" [matTooltipShowDelay]="600">
                    {{ group.template.status }}
                  </span>
                </div>

                <!-- VM attributes — icon + eyebrow label + value, 2-column grid -->
                <ng-container *ngIf="isVmTemplate(group.template)">
                  <div class="rdv-vm-attrs">
                    <div class="rdv-vm-attr" *ngIf="group.template.cloudProvider">
                      <mat-icon class="rdv-vm-attr-icon" matTooltip="Cloud Provider">cloud_queue</mat-icon>
                      <div class="rdv-vm-attr-body">
                        <span class="rdv-vm-attr-label">Provider</span>
                        <!-- Tooltip on every truncatable value (global pattern). -->
                        <span class="rdv-vm-attr-val"
                          [matTooltip]="getProviderName(group.template)" [matTooltipShowDelay]="300">
                          {{ getProviderName(group.template) }}
                        </span>
                      </div>
                    </div>
                    <div class="rdv-vm-attr" *ngIf="group.template.region">
                      <mat-icon class="rdv-vm-attr-icon" matTooltip="Region">location_on</mat-icon>
                      <div class="rdv-vm-attr-body">
                        <span class="rdv-vm-attr-label">Region</span>
                        <span class="rdv-vm-attr-val"
                          [matTooltip]="getRegionName(group.template)" [matTooltipShowDelay]="300">
                          {{ getRegionName(group.template) }}
                        </span>
                      </div>
                    </div>
                    <div class="rdv-vm-attr" *ngIf="group.template.size">
                      <mat-icon class="rdv-vm-attr-icon" matTooltip="VM Size">memory</mat-icon>
                      <div class="rdv-vm-attr-body">
                        <span class="rdv-vm-attr-label">Size</span>
                        <span class="rdv-vm-attr-val"
                          [matTooltip]="getSizeName(group.template)" [matTooltipShowDelay]="300">
                          {{ getSizeName(group.template) }}
                        </span>
                      </div>
                    </div>
                    <div class="rdv-vm-attr" *ngIf="group.template.os">
                      <mat-icon class="rdv-vm-attr-icon" matTooltip="Operating System">desktop_windows</mat-icon>
                      <div class="rdv-vm-attr-body">
                        <span class="rdv-vm-attr-label">OS</span>
                        <!-- OS name can be long ("Windows Server 2022 Datacenter") — always tooltip. -->
                        <span class="rdv-vm-attr-val"
                          [matTooltip]="getOsName(group.template)" [matTooltipShowDelay]="300">
                          {{ getOsName(group.template) }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!--
                    Software chips — hoverable with tooltip.
                    Backend sends metadata.sdks/editors/misc as raw ID strings.
                    getSoftwareName() tries master-data lookup first; falls back to
                    formatSoftwareId() which prettifies the raw ID (e.g. "java-jdk-21" → "Java Jdk 21").
                    [matTooltipShowDelay] uses property binding (number) for reliable coercion.
                  -->
                  <div class="rdv-chips-row" *ngIf="hasSoftware(group.template)">
                    <span *ngIf="group.template.sdkCount" class="rdv-chip rdv-chip--hoverable"
                      [matTooltip]="getSdkTooltip(group.template)" [matTooltipShowDelay]="300">
                      <mat-icon class="rdv-chip-icon">code</mat-icon>
                      {{ group.template.sdkCount }} SDK{{ group.template.sdkCount !== 1 ? 's' : '' }}
                    </span>
                    <span *ngIf="group.template.editorCount" class="rdv-chip rdv-chip--hoverable"
                      [matTooltip]="getEditorTooltip(group.template)" [matTooltipShowDelay]="300">
                      <mat-icon class="rdv-chip-icon">edit</mat-icon>
                      {{ group.template.editorCount }} Editor{{ group.template.editorCount !== 1 ? 's' : '' }}
                    </span>
                    <span *ngIf="group.template.miscCount" class="rdv-chip rdv-chip--hoverable"
                      [matTooltip]="getToolTooltip(group.template)" [matTooltipShowDelay]="300">
                      <mat-icon class="rdv-chip-icon">construction</mat-icon>
                      {{ group.template.miscCount }} Tool{{ group.template.miscCount !== 1 ? 's' : '' }}
                    </span>
                    <span *ngIf="group.template.dbCount" class="rdv-chip rdv-chip--hoverable"
                      [matTooltip]="getDatabaseTooltip(group.template)" [matTooltipShowDelay]="300">
                      <mat-icon class="rdv-chip-icon">storage</mat-icon>
                      {{ group.template.dbCount }} DB{{ group.template.dbCount !== 1 ? 's' : '' }}
                    </span>
                  </div>
                </ng-container>

                <!-- UA attributes — description only (status is inline with name) -->
                <p class="rdv-rg-desc"
                  *ngIf="isUaTemplate(group.template) && group.template.description">
                  {{ group.template.description }}
                </p>

                <!-- Generic fallback description -->
                <p class="rdv-rg-desc"
                  *ngIf="isGenericTemplate(group.template) && group.template.description">
                  {{ group.template.description }}
                </p>
              </div>

              <!-- Hairline divider -->
              <div class="rdv-rg-hairline"></div>

              <!-- ── Policy column ──────────────────────────────────────── -->
              <div class="rdv-rg-col">
                <div class="rdv-rg-col-heading">Policy</div>
                <div class="rdv-rg-col-rule"></div>

                <ng-container *ngIf="group.policy; else noPolicy">
                  <div class="rdv-rg-name" [matTooltip]="group.policy.name || '—'" [matTooltipShowDelay]="400">
                    {{ group.policy.name || '—' }}
                  </div>
                  <p class="rdv-rg-desc" *ngIf="group.policy.description">{{ group.policy.description }}</p>
                  <div class="rdv-kv-row rdv-kv-row--icon" *ngIf="group.policy.activeCreationPolicy">
                    <mat-icon class="rdv-kv-icon rdv-kv-icon--creation">add_circle_outline</mat-icon>
                    <span class="rdv-kv-label">{{ getCreationLabel(group.policy) }}</span>
                    <span class="rdv-kv-value"
                      [matTooltip]="group.policy.activeCreationPolicy" [matTooltipShowDelay]="300">
                      {{ group.policy.activeCreationPolicy }}
                    </span>
                  </div>
                  <div class="rdv-kv-row rdv-kv-row--icon" *ngIf="group.policy.activeDailyPolicy">
                    <mat-icon class="rdv-kv-icon rdv-kv-icon--daily">autorenew</mat-icon>
                    <span class="rdv-kv-label">Daily</span>
                    <span class="rdv-kv-value"
                      [matTooltip]="group.policy.activeDailyPolicy" [matTooltipShowDelay]="300">
                      {{ group.policy.activeDailyPolicy }}
                    </span>
                  </div>
                  <div class="rdv-kv-row rdv-kv-row--icon" *ngIf="group.policy.activeClassEndPolicy">
                    <mat-icon class="rdv-kv-icon rdv-kv-icon--end">stop_circle</mat-icon>
                    <span class="rdv-kv-label">Class End</span>
                    <span class="rdv-kv-value"
                      [matTooltip]="group.policy.activeClassEndPolicy" [matTooltipShowDelay]="300">
                      {{ group.policy.activeClassEndPolicy }}
                    </span>
                  </div>
                </ng-container>
                <ng-template #noPolicy>
                  <span class="rdv-no-policy">— no policy assigned —</span>
                </ng-template>
              </div>

            </div><!-- /.rdv-rg-columns -->
          </div><!-- /.rdv-rg-block -->
        </div><!-- /.rdv-rg-list -->
      </ng-container>

      <ng-template #noConfigs>
        <div class="rdv-empty-state">No templates assigned. Forward to Cloud Settings to assign templates.</div>
      </ng-template>
    </section>

  </div><!-- /.rdv-content -->

  <!-- Forward Popup -->
  <app-forward-requisition *ngIf="showForwardPopup" [requisitionId]="detailView.requisition.id"
    [selectedRequisition]="detailView.requisition" (closed)="showForwardPopup=false" (forwarded)="onForwarded($event)">
  </app-forward-requisition>

</ng-container>
